/**
 * Streamdown wrapper that surfaces wiki-links in agent output.
 *
 * An agent's system prompt can instruct it to use `[[Title]]` when referring to
 * project files. That syntax is invisible to streamdown's markdown parser on its
 * own — it renders as literal text — so we:
 *
 *   1. Run a `remarkWikiLinks` plugin that turns `[[Title]]` text into `link`
 *      nodes pointing at `/files/<target>`. Doing this at the AST layer leaves
 *      text inside `inlineCode` / `code` untouched, so examples like
 *      `` `[[Foo]]` `` survive verbatim. Emitting a plain relative URL (no
 *      custom scheme) means streamdown's sanitize + harden pipeline leaves the
 *      href intact.
 *   2. Hand any `/files/...` href to the host's {@link WikiLinkRenderer}, which
 *      knows its own router. A host that doesn't supply one gets an ordinary
 *      anchor, which is also what a non-wiki link always gets.
 */
import { code, createCodePlugin } from "@streamdown/code";
import { useMemo, type ReactNode } from "react";
import { Streamdown, defaultRemarkPlugins, type Components } from "streamdown";

// KaTeX styles beside the renderer, not the app entry: entry CSS skips them,
// every math-rendering chunk gets them by construction, and a sandboxed iframe's
// `index.css` injection stays math-free.
import "katex/dist/katex.min.css";

import { MATH_PLUGIN } from "./agent-math";
import { parseFileHref } from "./file-href";
import { remarkWikiLinks } from "./remark-wiki-links";

/**
 * Render one resolved wiki link. `target` is the raw `[[Target]]` text; the host
 * resolves it against its own file map and returns its router's link element.
 */
export type WikiLinkRenderer = (props: {
  target: string;
  children: ReactNode;
  /** Remaining anchor props, minus the ones a SPA link must not inherit. */
  rest: Record<string, unknown>;
}) => ReactNode;

interface AgentMarkdownProps {
  children: string;
  className?: string;
  wikiLink?: WikiLinkRenderer;
  /** Rendered on a surface that inverts the page's color scheme (the user bubble). */
  inverted?: boolean;
}

// Passing `remarkPlugins` to Streamdown *replaces* its defaults rather than
// merging, so we must re-include them or GFM (pipe tables, strikethrough, task
// lists, autolinks) and the code-meta plugin silently drop out. Spread the
// library's own exported defaults so this tracks any future changes, then add
// our wiki-link transform.
const REMARK_PLUGINS = [
  ...Object.values(defaultRemarkPlugins),
  remarkWikiLinks,
];

// [light, dark] swapped: an inverted surface is dark in light mode and vice versa.
const INVERTED_CODE = createCodePlugin({
  themes: ["github-dark", "github-light"],
});

export function AgentMarkdown({
  children,
  className,
  wikiLink,
  inverted = false,
}: AgentMarkdownProps) {
  const components = useMemo<Components>(
    () => ({
      a: ({
        href,
        children: anchor,
        node: _node,
        // streamdown's rehype-harden sets target=_blank + rel=noopener on
        // every link. That's the right default for an external `<a>`, but it
        // would force a new-tab open on an in-app link. Drop both when the
        // host takes the link over.
        target,
        rel,
        ...rest
      }) => {
        const wikiTarget = wikiLink ? parseFileHref(href) : null;
        if (wikiTarget === null) {
          return (
            <a href={href} target={target} rel={rel} {...rest}>
              {anchor}
            </a>
          );
        }
        return (
          <>{wikiLink?.({ target: wikiTarget, children: anchor, rest })}</>
        );
      },
    }),
    [wikiLink],
  );

  return (
    <Streamdown
      className={className}
      remarkPlugins={REMARK_PLUGINS}
      plugins={{ code: inverted ? INVERTED_CODE : code, math: MATH_PLUGIN }}
      components={components}
    >
      {children}
    </Streamdown>
  );
}
